import { action, computed, observable } from 'mobx';

import { Paginated, StringKeyMap } from '../../adl-gen/common';
import { DbKey, WithDbId } from '../../adl-gen/common/db';
import { TableQuery } from '../../adl-gen/common/tabular';
import {
  AppClient,
  Competency,
  I18nContent,
  Skill,
  WorkRole,
  WorkRoleScoreRange,
  WorkRoleSkill
} from '../../adl-gen/totalinteraction/futurefit/db';
import * as FE from '../../adl-gen/totalinteraction/futurefit/frontend';
import {
  CloneWorkRoleReq,
  CreateWorkRoleReq,
  makeCloneWorkRoleReq,
  makeCreateWorkRoleReq,
  makeUpdateQuizReq,
  makeUpdateWorkRoleReq,
  UpdateQuizReq,
  UpdateWorkRoleReq,
  WorkRoleReferenceData
} from '../../adl-gen/totalinteraction/futurefit/requests';
import { TranslateFn } from '../../internationalization/i18n-hoc';
import { fromPromise, MaybeLoading } from '../../loader/loader';
import { Service } from '../../service/service';
import { LoggedIn, LoggedInSuper } from '../../stores/identity-store';
import { Plot, plotOrderKeys, PlotType } from '../../stores/quiz-results-store';
import { average, compareByGet, objectIdsValues } from '../../util/utils';

import { DataStore, TransportLayer } from './datastore';
import { QuizListEntry } from '../ui/components/lists/quiz-list/quiz-list-view';

export type ScoreRangeUpdate =
  | {
      kind: 'create';
      value: WorkRoleScoreRange;
    }
  | {
      kind: 'update';
      value: WithDbId<WorkRoleScoreRange>;
    }
  | {
      kind: 'delete';
      value: DbKey<WorkRoleScoreRange>;
    };

export interface WorkRoleUpdate {
  workRole: WithDbId<WorkRole>;
  scoreRangeUpdates: ScoreRangeUpdate[];
}

export type RoleBuilderPlot = Pick<Plot, 'expected' | 'industry'>;

export interface ResultsByWorkRole {
  [key: string]: RoleBuilderPlot & { name: I18nContent };
}

type RoleBuilderPlotType = Exclude<PlotType, { kind: 'overall' }>;

/// Used in RoleBuilderForm and Editor
export type LoadedMap = Map<DbKey<Skill>, FE.Available<Skill, WorkRoleSkill>>;
export type ChangesMap = Record<DbKey<Skill>, FE.ChangeState<WorkRoleSkill>>;

export class RoleBuilderHelper {
  private readonly assignedCompetencies: StringKeyMap<DbKey<Competency>, Competency> = {};
  private readonly availableCompetencies: StringKeyMap<DbKey<Competency>, Competency> = {};
  private readonly assignedSkills: StringKeyMap<DbKey<Skill>, Skill> = {};
  private readonly assignedSkillsByCompetency: StringKeyMap<DbKey<Competency>, DbKey<Skill>[]> = {};
  private readonly workRoleSkillMap: StringKeyMap<DbKey<Skill>, WithDbId<WorkRoleSkill>> = {};
  private readonly availableSkillMap: StringKeyMap<DbKey<Competency>, FE.Available<Skill, WorkRoleSkill>[]> = {};
  // private readonly quizzes: FE.WorkRoleQuizData[]
  readonly ranges: WithDbId<WorkRoleScoreRange>[];

  private readonly results: ResultsByWorkRole = {};

  // private readonly roles: ComparisonRoles

  // private readonly providerIds?: DbKey<LearningProvider>[]

  // @observable
  // private selectedRoleIds: DbKey<WorkRole>[]

  private readonly plotOrder: (keyof RoleBuilderPlot)[];

  // TODO: Role list, then client list, provider list, range lists all from QueryRoleBuilderData
  public static fromRoleBuilderData(
    workrole: FE.WorkRoleListEntry,
    // One role
    roleBuilderData: FE.RoleBuilderData
    // roles: ComparisonRoles,
    // providerIds: DbKey<LearningProvider>[],
    // selectedRoleIds?: string[],
  ): RoleBuilderHelper {
    return new RoleBuilderHelper(workrole, roleBuilderData);
  }

  private constructor(readonly workrole: FE.WorkRoleListEntry, readonly roleBuilderData: FE.RoleBuilderData) {
    /// Sanity check
    if (workrole.id.localeCompare(roleBuilderData.role) !== 0) {
      throw new Error('RoleBuilderHelper initialized with inconsistent WorkRole Ids');
    }

    const assignedSkills: FE.Assigned<Skill, WorkRoleSkill>[] = [];

    this.availableCompetencies = roleBuilderData.competencies.reduce(
      (acc: StringKeyMap<DbKey<Competency>, Competency>, next) => {
        acc[next.id] = next.value;
        return acc;
      },
      {}
    );

    /// Just one loop
    for (const availableSkill of roleBuilderData.availableSkills) {
      const skill = availableSkill.value.attribute;

      if (availableSkill.kind === 'assigned') {
        // 1. skillsByCompetencyMap - NOT unassigned
        if (Object.keys(this.assignedSkillsByCompetency).includes(skill.value.competencyId)) {
          this.assignedSkillsByCompetency[skill.value.competencyId].push(skill.id);
        } else {
          this.assignedSkillsByCompetency[skill.value.competencyId] = [skill.id];
        }

        // 2. competencies NOT unassigned
        this.assignedCompetencies = roleBuilderData.competencies.reduce(
          (acc: StringKeyMap<DbKey<Competency>, Competency>, next) => {
            if (Object.keys(this.assignedSkillsByCompetency).includes(next.id)) {
              acc[next.id] = next.value;
            }
            return acc;
          },
          {}
        );

        // 3. skills NOT unassigned
        this.assignedSkills[skill.id] = skill.value;

        // 4. AssignedSkills
        assignedSkills.push(availableSkill.value);

        // 5. WorkRoleSkill
        this.workRoleSkillMap[skill.id] = availableSkill.value.item;
      }

      // 6. All availableSkills incl unassigned
      if (Object.keys(this.availableSkillMap).includes(skill.value.competencyId)) {
        this.availableSkillMap[skill.value.competencyId].push(availableSkill);
      } else {
        this.availableSkillMap[skill.value.competencyId] = [availableSkill];
      }
    }

    this.results[workrole.id] = {
      name: workrole.name,
      expected: this.buildResultScoreSet('expected', assignedSkills),
      industry: this.buildResultScoreSet('industry', assignedSkills),
      // comparisonRole: undefined,
    };

    this.ranges = workrole.scoreRanges;

    this.plotOrder = Object.entries(plotOrderKeys)
      .filter(([key, _]) => !['difference', 'actual', 'previous', 'group'].includes(key))
      .map(([plotkey, _]) => plotkey) as (keyof RoleBuilderPlot)[];
  }

  private readonly buildResultScoreSet = (
    title: 'expected' | 'industry',
    assignedSkills: FE.Assigned<Skill, WorkRoleSkill>[]
  ) => {
    const byCompetency: StringKeyMap<string, number[]> = {};

    assignedSkills.forEach(assignedSkill => {
      const workRoleSkill = assignedSkill.item.value;
      const { competencyId: id } = assignedSkill.attribute.value;
      Object.keys(byCompetency).includes(id)
        ? byCompetency[id].push(workRoleSkill[title])
        : (byCompetency[id] = [workRoleSkill[title]]);
    });

    return FE.makeResultsScoreSet({
      title,
      overall: average(assignedSkills.map(as => as.item.value[title])),
      byCompetency: Object.entries(byCompetency).reduce((acc: StringKeyMap<string, number>, [id, arr]) => {
        acc[id] = average(arr);
        return acc;
      }, {}),
      bySkill: assignedSkills.reduce((acc: StringKeyMap<string, number>, next) => {
        const workroleSkill = next.item.value;
        acc[workroleSkill.skillId] = workroleSkill[title];
        return acc;
      }, {})
    });
  };

  private dataset(plot: keyof Plot): FE.ResultsScoreSet[] {
    const raw = Object.values(this.results).map(results => results[plot]);
    // Some keys (like previous) aren't always populated
    // tslint:disable-next-line: strict-type-predicates
    return raw.filter(r => r !== undefined) as FE.ResultsScoreSet[];
  }

  private getScoreFromType(type: RoleBuilderPlotType): (t: FE.ResultsScoreSet) => number | undefined {
    if (type.kind === 'competency') {
      return t => t.byCompetency[type.competencyId];
    } else if (type.kind === 'skill') {
      return t => t.bySkill[type.skillId];
    }
    throw Error('Unimplemented property type');
  }

  score(plot: keyof Plot, type: RoleBuilderPlotType): number {
    const scores = this.dataset(plot)
      .map(this.getScoreFromType(type))
      .filter(num => num !== undefined);

    return average(scores);
  }

  expected(type: RoleBuilderPlotType) {
    return this.score('expected', type);
  }

  assignedCompetency(competencyId: DbKey<Competency>) {
    return this.assignedCompetencies[competencyId];
  }

  assignedSkill(skillId: DbKey<Skill>): Skill | undefined {
    return this.assignedSkills[skillId];
  }

  /// cf. competenciesWithDbId
  private assignedSkillsWithDbId(xlat?: TranslateFn) {
    return objectIdsValues(this.assignedSkills).sort(compareByGet(t => (xlat ? xlat(t.value.name) : t.value.name)));
  }

  /// Use arrow => otherwise this may be unbound unless passed in
  getAssignedSkillIdsByCompetency = (competencyId: DbKey<Competency>): DbKey<Skill>[] | undefined => {
    return this.assignedSkillsByCompetency[competencyId];
  };

  getAssignedSkillsByCompetency = (
    competencyId: DbKey<Competency>,
    xlat?: TranslateFn
  ): WithDbId<Skill>[] | undefined => {
    const assignedSkillIds = this.assignedSkillsByCompetency[competencyId];
    if (assignedSkillIds) {
      return this.assignedSkillsWithDbId(xlat).filter(s => assignedSkillIds.includes(s.id));
    } else {
      return undefined;
    }
  };

  getAvailableSkillsForCompetency = (competencyId: DbKey<Competency>) => {
    return this.availableSkillMap[competencyId];
  };

  getAvailableSkills = () => {
    return Object.values(this.availableSkillMap);
  };

  private availableCompetenciesWithDbId(xlat?: TranslateFn) {
    return objectIdsValues(this.availableCompetencies).sort(
      xlat ? compareByGet(t => (xlat ? xlat(t.value.name) : t.value.name)) : compareByGet(t => t.value.ordering)
    );
  }

  getAvailableCompetenciesWithKeys = (xlat?: TranslateFn) => {
    return this.availableCompetenciesWithDbId(xlat);
  };

  /// competencies with keys, sorted:
  private assignedCompetenciesWithDbId(xlat?: TranslateFn) {
    return objectIdsValues(this.assignedCompetencies).sort(
      xlat ? compareByGet(t => (xlat ? xlat(t.value.name) : t.value.name)) : compareByGet(t => t.value.ordering)
    );
  }

  /// competency keys sorted - needs to be arrow func to bind _this_
  getAssignedCompetencyKeys(xlat?: TranslateFn): DbKey<Competency>[] {
    return this.assignedCompetenciesWithDbId(xlat).map(cid => cid.id);
  }

  /// competencies sorted
  getAssignedCompetencies(xlat?: TranslateFn): Competency[] {
    return this.assignedCompetenciesWithDbId(xlat).map(c => c.value);
  }

  getWorkRoleSkillforSkill(id: DbKey<Skill>): WithDbId<WorkRoleSkill> | undefined {
    return this.workRoleSkillMap[id];
  }

  getWorkRoleSkills(): WithDbId<WorkRoleSkill>[] {
    return Object.values(this.workRoleSkillMap);
  }

  private areRolesSelected(): boolean {
    return false;
    // TODO:
    // return this.getSelectedRoleIds().length > 0
  }

  getPlotKeys(): (keyof RoleBuilderPlot)[] {
    if (this.areRolesSelected()) {
      const keys: (keyof RoleBuilderPlot)[] = ['expected'];
      return keys;
    }
    const keySet = new Set<keyof RoleBuilderPlot>();
    for (const plot of Object.values(this.results)) {
      this.plotOrder
        .filter(k => plot[k] !== undefined)
        .filter(k => k !== 'industry')
        .forEach(k => keySet.add(k));
    }
    return Array.from(keySet.keys());
  }

  plotExists(plotkey: keyof RoleBuilderPlot): boolean {
    return this.getPlotKeys().includes(plotkey);
  }

  setRadii = (): number[] => {
    /// Return default if # ranges is < 1 or > 5
    const defaultRadii = [20, 40, 60, 80, 100];
    /// Copy ranges, map to just max values, sort
    const ranges = this.ranges
      .slice()
      .map(r => r.value.scoreRange.range.v2)
      .sort((a, b) => a - b);
    if (ranges.length < 1 || ranges.length > 5) {
      return defaultRadii;
    } else {
      /// Find their gaps as percent; set ranges accordingly
      const highestMax = ranges[ranges.length - 1];
      return ranges.map(r => Math.floor((r / highestMax) * 100));
    }
  };
}

// Service, excluding WorkRoleSkill CRUD, is safe for client admin access
class WorkRoleApi implements TransportLayer<WorkRole> {
  kind: string = 'WorkRole';
  constructor(protected readonly service: Service) {}
  create = async (item: WorkRole): Promise<DbKey<WorkRole>> => this.service.createWorkRole(item);
  update = async (itemWithId: WithDbId<WorkRole>): Promise<void> => this.service.updateWorkRole(itemWithId);
  doDelete = async (id: DbKey<WorkRole>): Promise<void> => this.service.deleteWorkRole(id);
  query = async (q: TableQuery): Promise<Paginated<WithDbId<WorkRole>>> => this.service.queryWorkRoles(q);

  createScoreRanges = async (ranges: WorkRoleScoreRange[]): Promise<void> => {
    await Promise.all(ranges.map(r => this.service.createWorkRoleScoreRange(r)));
  };
  updateScoreRanges = async (ranges: WithDbId<WorkRoleScoreRange>[]): Promise<void> => {
    await Promise.all(ranges.map(r => this.service.updateWorkRoleScoreRange(r)));
  };
  deleteScoreRanges = async (ranges: DbKey<WorkRoleScoreRange>[]): Promise<void> => {
    await Promise.all(ranges.map(r => this.service.deleteWorkRoleScoreRange(r)));
  };
  queryScoreRanges = async (q: TableQuery): Promise<Paginated<WithDbId<WorkRoleScoreRange>>> => {
    const scoreRanges = await this.service.queryWorkRoleScoreRanges(q);
    return scoreRanges;
  };
  queryWorkRoleList = async (q: TableQuery): Promise<Paginated<FE.WorkRoleListEntry>> => {
    const workRoleList = await this.service.queryWorkRoleList(q);
    return workRoleList;
  };
  queryWorkRoleReferenceData = async (): Promise<WorkRoleReferenceData> => {
    const workRoleData = await this.service.queryWorkRoleReferenceData();
    return workRoleData;
  };
  createWorkRoleWithRanges = async (req: CreateWorkRoleReq) => {
    return this.service.createWorkRoleWithRanges(req);
  };
  updateWorkRoleWithRanges = async (req: UpdateWorkRoleReq) => {
    return this.service.updateWorkRoleWithRanges(req);
  };
  deleteWorkRoleWithRanges = async (req: DbKey<WorkRole>) => {
    return this.service.deleteWorkRoleWithRanges(req);
  };
  cloneWorkRole = async (req: CloneWorkRoleReq) => {
    return this.service.cloneWorkRole(req);
  };
  queryRoleBuilderData = async (roleId: DbKey<WorkRole>): Promise<FE.RoleBuilderData> =>
    this.service.queryRoleBuilderData(roleId);
  updateRoleBuilderData = async (data: FE.RoleBuilderUpdates) => this.service.updateRoleBuilderData(data);
  updateQuiz = async (req: UpdateQuizReq): Promise<void> => this.service.updateQuiz(req);
}
export class WorkRoleStore extends DataStore<WorkRole> {
  @observable.ref list?: Promise<Paginated<FE.WorkRoleListEntry>>;

  workRoleData: Promise<WorkRoleReferenceData>;

  @observable clientId?: DbKey<AppClient>;

  @observable.ref roleBuilderData?: Promise<FE.RoleBuilderData>;

  @observable selectedEntry?: Promise<FE.WorkRoleListEntry>;

  public static fromService = (
    service: Service,
    identity: LoggedIn | LoggedInSuper,
    appClientId?: DbKey<AppClient>,
    id?: DbKey<WorkRole>
  ): WorkRoleStore => {
    const store = new WorkRoleStore(new WorkRoleApi(service), identity, appClientId, id);
    return store;
  };

  public constructor(
    transport: TransportLayer<WorkRole>,
    identity: LoggedIn | LoggedInSuper,
    appClientId?: DbKey<AppClient>,
    id?: DbKey<WorkRole>
  ) {
    super(transport, identity, id);
    this.clientId = appClientId;
    // Don't autoload all data, we might only want a subset - hence not in base class
    this.data = this.init(id);
    this.workRoleData = this.initData();

    if (id) {
      this.selectedEntry = this.authorized ? this.selectEntry(id) : Promise.reject('Not authorized');
      this.roleBuilderData = this.queryRoleBuilderData(id);
    }
    this.list = this.initList();
  }

  /// Near-override of base.select
  private readonly selectEntry = async (id: DbKey<WorkRole>): Promise<FE.WorkRoleListEntry> => {
    const results = await this.queryList(this.queryId(id));
    if (results.total_size !== 1) {
      return Promise.reject(`${this.transport.kind} with id ${id} not found/invalid query`);
    }
    return results.items[0];
  };

  /// Near-override of base.setSelected
  @action
  public setSelectedRole = async (id: DbKey<WorkRole>): Promise<void> => {
    if (this.authorized) {
      await Promise.resolve((this.selectedEntry = this.selectEntry(id))).then(
        _resolve => (this.roleBuilderData = this.queryRoleBuilderData(id))
      );
    } else {
      return Promise.reject('Not authorized');
    }
  };

  private readonly initList = async (_id?: DbKey<WorkRole>): Promise<Paginated<FE.WorkRoleListEntry>> => {
    if (this.authorized) {
      const results = await this.loadList();
      return results;
    } else {
      return Promise.reject('Not authorized');
    }
  };

  private readonly initData = async (): Promise<WorkRoleReferenceData> => {
    if (this.authorized) {
      const workRoleData = this.loadWorkRoleReferenceData();
      return workRoleData;
    } else {
      return Promise.reject('Not authorized');
    }
  };

  private readonly loadList = async (_id?: DbKey<WorkRole>): Promise<Paginated<FE.WorkRoleListEntry>> => {
    // if (id) {
    //     await this.setSelectedEntry(id)
    // }
    // const selection = await this.selectedEntry
    const results = await this.queryList(
      // selection ? this.queryId(selection.id) :
      this.queryAll
    );
    return results;
  };

  private readonly loadWorkRoleReferenceData = async (): Promise<WorkRoleReferenceData> => {
    if (this.authorized) {
      const results = await this.queryData();
      return results;
    } else {
      return Promise.reject('Not authorized');
    }
  };

  private readonly queryList = async (q: TableQuery): Promise<Paginated<FE.WorkRoleListEntry>> => {
    const result = await (this.transport as WorkRoleApi).queryWorkRoleList(q);
    return result;
  };

  private readonly queryData = async (): Promise<WorkRoleReferenceData> => {
    const result = await (this.transport as WorkRoleApi).queryWorkRoleReferenceData();
    return result;
  };

  private readonly queryRoleBuilderData = async (roleId: DbKey<WorkRole>): Promise<FE.RoleBuilderData> => {
    const data = (this.transport as WorkRoleApi).queryRoleBuilderData(roleId);
    return data;
  };

  @computed
  public get selectedRole(): MaybeLoading<FE.WorkRoleListEntry> | undefined {
    if (this.authorized) {
      if (this.selectedEntry) {
        return fromPromise(this.selectedEntry);
      } else {
        return undefined;
      }
    } else {
      return { kind: 'error', error: 'Not authorized' };
    }
  }

  @computed
  get loadingList(): MaybeLoading<Paginated<FE.WorkRoleListEntry>> {
    if (this.authorized) {
      if (this.list) {
        return fromPromise(this.list);
      } else {
        return { kind: 'loading' };
      }
    } else {
      return { kind: 'error', error: 'Not authorized' };
    }
  }

  @computed
  public get loadingData(): MaybeLoading<WorkRoleReferenceData> {
    if (this.authorized) {
      return fromPromise(this.workRoleData);
    } else {
      return { kind: 'error', error: 'Not authorized' };
    }
  }

  @computed
  public get loadingRoleBuilderData(): MaybeLoading<FE.RoleBuilderData> {
    if (this.authorized) {
      if (this.roleBuilderData) {
        return fromPromise(this.roleBuilderData);
      } else {
        return { kind: 'loading' };
      }
    } else {
      return { kind: 'error', error: 'Not authorized' };
    }
  }

  queryScoreRanges = async (q: TableQuery): Promise<Paginated<WithDbId<WorkRoleScoreRange>>> => {
    const result = (this.transport as WorkRoleApi).queryScoreRanges(q);
    return result;
  };

  public updateRoleBuilderData = async (data: FE.RoleBuilderUpdates): Promise<void> => {
    await (this.transport as WorkRoleApi).updateRoleBuilderData(data);
  };

  // Create role, then ranges
  createWithRanges = async (item: WorkRoleUpdate): Promise<DbKey<WorkRole>> => {
    const req = makeCreateWorkRoleReq({
      workRole: item.workRole.value,
      ranges: item.scoreRangeUpdates.reduce((acc: WorkRoleScoreRange[], range) => {
        /// Should only be create ranges... And we have to pass a dummy roleId
        if (range.kind === 'create') {
          range.value.workRoleId = '';
          acc.push(range.value);
        }
        return acc;
      }, [])
    });
    const roleId = await (this.transport as WorkRoleApi).createWorkRoleWithRanges(req);
    return roleId;
  };

  // WorkRole is not being created or deleted
  updateWithRanges = async (item: WorkRoleUpdate): Promise<void> => {
    const updates: WithDbId<WorkRoleScoreRange>[] = [];
    const creates: WorkRoleScoreRange[] = [];
    const deletes: DbKey<WorkRoleScoreRange>[] = [];
    item.scoreRangeUpdates.slice().forEach(range => {
      switch (range.kind) {
        case 'update':
          {
            updates.push({ ...range.value });
          }
          break;
        case 'create':
          {
            range.value.workRoleId = item.workRole.id;
            creates.push({ ...range.value });
          }
          break;
        default:
          deletes.push(range.value);
      }
    });
    const req = makeUpdateWorkRoleReq({
      workRole: item.workRole,
      creates,
      updates,
      deletes
    });
    await (this.transport as WorkRoleApi).updateWorkRoleWithRanges(req);
  };

  // Delete ranges, then roles - query the ranges first
  // NOTE: unused
  _deleteWithRanges = async (role: DbKey<WorkRole>): Promise<void> => {
    await (this.transport as WorkRoleApi).deleteWorkRoleWithRanges(role);
  };

  cloneWorkRole = async (item: WorkRoleUpdate): Promise<DbKey<WorkRole>> => {
    const req = makeCloneWorkRoleReq({
      originalId: item.workRole.id,
      workRole: item.workRole.value,
      ranges: item.scoreRangeUpdates.reduce((acc: WorkRoleScoreRange[], range) => {
        /// Should only be create ranges...
        if (range.kind === 'create') {
          range.value.workRoleId = item.workRole.id;
          acc.push(range.value);
        }
        return acc;
      }, [])
    });
    const roleId = await (this.transport as WorkRoleApi).cloneWorkRole(req);
    return roleId;
  };

  updateQuiz = async (item: QuizListEntry): Promise<void> => {
    const req = makeUpdateQuizReq({
      id: item.id,
      title: null,
      imageUrl: null,
      workRoleId: null,
      workRole: null,
      lockResults: item.lockResults
    });
    await (this.transport as WorkRoleApi).updateQuiz(req);
  }
}



// WEBPACK FOOTER //
// ./src/admin/stores/work-role-store.ts